import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { getCurrencySymbol } from '../../../utils/currency';
import { uploadReceiptFile } from '../../../utils/fileUpload';
import api from '../../../lib/api';

const ExpenseModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  expense,
  categories,
  vendors,
  paymentMethods
}) => {
  const [formData, setFormData] = useState({
    date: '',
    vendor: '',
    category: '',
    amount: '',
    paymentMethod: '',
    referenceNumber: '',
    description: '',
    taxDeductible: false,
    receipt: null,
    project: ''
  });

  const [receiptPreview, setReceiptPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    if (expense) {
      setFormData({
        date: expense?.date,
        vendor: expense?.vendor,
        category: expense?.category,
        amount: expense?.amount?.toString(),
        paymentMethod: expense?.paymentMethod,
        referenceNumber: expense?.referenceNumber || '',
        description: expense?.description || '',
        taxDeductible: expense?.taxDeductible || false,
        receipt: null,
        project: expense?.project || ''
      });
      // Set receipt preview from existing expense
      setReceiptPreview(expense?.receiptUrl || expense?.receipt);
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        category: '',
        amount: '',
        paymentMethod: '',
        referenceNumber: '',
        description: '',
        taxDeductible: false,
        receipt: null,
        project: ''
      });
      setReceiptPreview(null);
    }
    setErrors({});
  }, [expense, isOpen]);

  // Fetch projects for project dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await api.get('/api/Expenses/projects');
        console.log('Projects API response:', response.data);
        
        // Handle API response format: { Success, Message, Data, Errors }
        let projectsData = [];
        if (response.data?.Success && Array.isArray(response.data?.Data)) {
          projectsData = response.data.Data;
        } else if (response.data?.success && Array.isArray(response.data?.data)) {
          projectsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          projectsData = response.data;
        }
        
        const projectOptions = projectsData.map(item => ({
          value: item.ProjectName || item.projectName,
          label: item.ProjectName || item.projectName
        }));
        
        setProjects([
          { value: '', label: 'Select a project' },
          ...projectOptions
        ]);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([{ value: '', label: 'Select a project' }]);
      } finally {
        setLoadingProjects(false);
      }
    };

    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    console.log(`Field changed: ${field} = ${value}`);
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      console.log('Updated formData:', newData);
      return newData;
    });
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, receipt: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader?.result);
        // Skip OCR simulation for now
        console.log('File selected for upload (upload disabled):', file.name);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemoveReceipt = () => {
    setFormData(prev => ({ ...prev, receipt: null }));
    setReceiptPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.date) newErrors.date = 'Date is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.amount || parseFloat(formData?.amount) <= 0) newErrors.amount = 'Valid amount is required';
    if (!formData?.paymentMethod) newErrors.paymentMethod = 'Payment method is required';
    if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('Form submission - formData before validation:', formData);
    
    if (validateForm()) {
      // Skip file upload for now - just use a placeholder or empty string
      let receiptUrl = '';
      
      // If there's an existing receipt URL from editing, keep it
      if (receiptPreview && !formData.receipt) {
        receiptUrl = expense?.receiptUrl || receiptPreview || '';
      }
      
      // For new file uploads, we'll skip the upload and just note that a file was selected
      if (formData.receipt && formData.receipt instanceof File) {
        console.log('File upload skipped - would upload:', formData.receipt.name);
        receiptUrl = `placeholder_receipt_${formData.receipt.name}`;
      }

      const submissionData = {
        ...formData,
        amount: parseFloat(formData?.amount),
        id: expense?.id || Date.now(),
        status: expense?.status || 'pending',
        receiptUrl: receiptUrl
      };
      
      console.log('Form submission - final data being sent:', submissionData);
      onSave(submissionData);
      onClose();
    }
  };



  if (!isOpen) return null;

  console.log('ExpenseModal render - formData:', formData);
  console.log('ExpenseModal render - projects:', projects);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {expense ? 'Edit Expense' : 'Add New Expense'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              type="date"
              label="Expense Date"
              required
              value={formData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              error={errors?.date}
            />

            <Input
              type="text"
              label="Vendor"
              placeholder="Enter vendor name"
              value={formData?.vendor}
              onChange={(e) => handleInputChange('vendor', e?.target?.value)}
              error={errors?.vendor}
              list="vendor-suggestions"
            />
            <datalist id="vendor-suggestions">
              {vendors.filter(vendor => vendor.value !== 'all').map(vendor => (
                <option key={vendor.value} value={vendor.value} />
              ))}
            </datalist>

            <Select
              label="Category"
              required
              options={categories}
              value={formData?.category}
              onChange={(value) => handleInputChange('category', value)}
              error={errors?.category}
              placeholder="Select expense category"
            />

            <Input
              type="number"
              label={`Amount ${getCurrencySymbol()}`}
              required
              placeholder="0.00"
              value={formData?.amount}
              onChange={(e) => handleInputChange('amount', e?.target?.value)}
              error={errors?.amount}
            />

            <Select
              label="Payment Method"
              required
              options={paymentMethods}
              value={formData?.paymentMethod}
              onChange={(value) => handleInputChange('paymentMethod', value)}
              error={errors?.paymentMethod}
            />

            <Input
              type="text"
              label="Reference Number"
              placeholder="Enter reference number"
              value={formData?.referenceNumber}
              onChange={(e) => handleInputChange('referenceNumber', e?.target?.value)}
              error={errors?.referenceNumber}
            />

            <Select
              label="Project"
              options={projects}
              value={formData?.project}
              onChange={(value) => handleInputChange('project', value)}
              placeholder={loadingProjects ? "Loading projects..." : "Select a project"}
              disabled={loadingProjects}
              searchable={true}
            />
          </div>

          <div className="mt-4 md:mt-6">
            <Input
              type="text"
              label="Description"
              required
              placeholder="Enter expense description..."
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              error={errors?.description}
            />
          </div>

          <div className="mt-4 md:mt-6">
            <Checkbox
              label="Tax Deductible"
              description="Mark this expense as tax deductible"
              checked={formData?.taxDeductible}
              onChange={(e) => handleInputChange('taxDeductible', e?.target?.checked)}
            />
          </div>

          <div className="mt-4 md:mt-6">
            <label className="block font-body font-medium text-sm text-foreground mb-2">
              Receipt
            </label>
            {receiptPreview ? (
              <div className="relative border-2 border-dashed border-border rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="w-32 h-32 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                    <Image
                      src={receiptPreview}
                      alt="Receipt preview showing uploaded expense documentation with transaction details"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-foreground mb-2">Receipt uploaded successfully</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveReceipt}
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Remove Receipt
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-border rounded-lg p-6 md:p-8 text-center hover:border-primary/50 transition-smooth">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="receipt-upload"
                />
                <label htmlFor="receipt-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Upload" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-sm md:text-base text-foreground mb-1">
                        Upload Receipt
                      </p>
                      <p className="caption text-muted-foreground">
                        Click to browse or drag and drop
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 mt-6 md:mt-8 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
              className="sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              fullWidth
              className="sm:w-auto"
            >
              {expense ? 'Update Expense' : 'Save Expense'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal;